/**
 * @module RecruitingDetail
 * @description Bewerber-Detailansicht — 70/30 Split, dynamic Pipeline-Stepper from DB,
 *   Compose (Email/SMS/Notiz), Dokumente-Upload, Interviews, Timeline, Sidebar Cards.
 * @route /recruiting/:id
 * @since v2.219.0
 * @updated v2.236.0 — Komplett-Neuaufbau: DB-Pipeline, Dokumente, Interviews, Template-Email
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const fj = (url, body, method = 'POST') => f(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

/** Strip HTML tags + CSS blocks from email body for timeline preview */
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days}T`;
  return `vor ${Math.floor(days / 30)}M`;
}

// ─── SVG Icons ───
const IconArrowRight = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>;
const IconMessageSquare = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const IconMail = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const IconMailOpen = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6z"/><path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10"/></svg>;
const IconSmartphone = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>;
const IconFileText = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>;
const IconPhone = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const IconClipboard = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/></svg>;
const IconUserCheck = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>;
const IconFileSignature = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 19.5v.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8.5L18 5.5"/><polyline points="14 2 14 8 20 8"/></svg>;
const IconRocket = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/></svg>;
const IconAlertTriangle = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>;
const IconSend = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>;
const IconStar = ({ filled }) => <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IconDownload = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>;
const IconCheck = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconUpload = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>;
const IconCalendar = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>;
const IconTrash = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>;
const IconCheckCircle = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const IconXCircle = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>;
const IconClock = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;

// #959 Bundle A: Status = Single Source of Truth (Spec aus FlexBewerberDetail).
// 7 Status-Werte mit fester Reihenfolge für Stepper.
const STATUS_DEF = [
  { key: 'beworben',   color: '#338edc', bg: '#EBF4FD' },
  { key: 'in_review',  color: '#F59E0B', bg: '#FEF3C7' },
  { key: 'eingeladen', color: '#22D3EE', bg: '#CFFAFE' },
  { key: 'zugesagt',   color: '#22D3EE', bg: '#CFFAFE' },
  { key: 'aktiv',      color: '#10B981', bg: '#D1FAE5' },
];
const ENDSTATUS = new Set(['abgelehnt', 'inaktiv']);
const STATUS_MAP = Object.fromEntries(STATUS_DEF.map(s => [s.key, s]));
const STATUS_ALIASES = { in_pruefung: 'in_review', eingegangen: 'beworben' };
const canonicalStatus = (s) => {
  if (!s) return null;
  const stripped = s.replace(/^flex_/, '');
  return STATUS_ALIASES[stripped] || stripped;
};

const AKT_CONFIG = {
  status_change: { icon: IconArrowRight, color: '#338edc', bg: '#EBF4FD' },
  notiz:         { icon: IconMessageSquare, color: '#6B7280', bg: '#F3F4F6' },
  email_out:     { icon: IconMail, color: '#6366F1', bg: '#EEF2FF' },
  email_in:      { icon: IconMailOpen, color: '#06B6D4', bg: '#ECFEFF' },
  email:         { icon: IconMail, color: '#6366F1', bg: '#EEF2FF' },
  sms_out:       { icon: IconSmartphone, color: '#10B981', bg: '#ECFDF5' },
  sms_in:        { icon: IconSmartphone, color: '#06B6D4', bg: '#ECFEFF' },
  sms:           { icon: IconSmartphone, color: '#10B981', bg: '#ECFDF5' },
  dokument:      { icon: IconFileText, color: '#D97706', bg: '#FFFBEB' },
  interview:     { icon: IconCalendar, color: '#338edc', bg: '#EBF4FD' },
  anruf:         { icon: IconPhone, color: '#10B981', bg: '#ECFDF5' },
  probearbeit:   { icon: IconClipboard, color: '#D97706', bg: '#FFFBEB' },
  stammdaten:    { icon: IconUserCheck, color: '#10B981', bg: '#ECFDF5' },
  vertrag:       { icon: IconFileSignature, color: '#6366F1', bg: '#EEF2FF' },
  onboarding:    { icon: IconRocket, color: '#338edc', bg: '#EBF4FD' },
  fehler:        { icon: IconAlertTriangle, color: '#EF4444', bg: '#FEF2F2' },
};

export default function RecruitingDetail() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  const [bewerber, setBewerber] = useState(null);
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Compose
  const [composeTab, setComposeTab] = useState('email');
  const [emailBetreff, setEmailBetreff] = useState('');
  const [emailText, setEmailText] = useState('');
  const [smsText, setSmsText] = useState('');
  const [notizText, setNotizText] = useState('');
  const [composeSending, setComposeSending] = useState(false);

  // Action modals
  const [showAbsage, setShowAbsage] = useState(false);
  const [absageGrund, setAbsageGrund] = useState('');
  const [showInterview, setShowInterview] = useState(false);
  const [showVertrag, setShowVertrag] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Unterprozess
  const [unterprozess, setUnterprozess] = useState(null);

  // Absage enhanced
  const [absageKategorie, setAbsageKategorie] = useState('');
  const [absageKommentar, setAbsageKommentar] = useState('');
  const [absageEmailSenden, setAbsageEmailSenden] = useState(true);

  // Onboarding success modal
  const [onboardingSuccess, setOnboardingSuccess] = useState(null);

  // Probearbeit
  const [hoverStar, setHoverStar] = useState(0);

  // Document upload
  const fileInputRef = useRef(null);

  // ─── Load data ───
  // #959 Bundle A: Pipeline-Fetch entfernt, Status ist Single Source of Truth.
  const load = useCallback(async () => {
    try {
      const bRes = await f(`/api/recruiting/bewerber/${id}`);
      if (bRes.ok) {
        const data = await bRes.json();
        setBewerber(data.bewerber);
        setAktivitaeten(data.aktivitaeten || []);
        setInterviews(data.interviews || []);
        setDokumente(data.dokumente || []);
      }
      try {
        const uRes = await f(`/api/recruiting/${id}/unterprozess`);
        if (uRes.ok) setUnterprozess(await uRes.json());
      } catch { /* */ }
    } catch { /* */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // #1845: Anrede und Signatur stehen sichtbar im Editor, statt vom Server
  //   unsichtbar angehaengt zu werden. Nur vorbelegen, solange das Feld leer ist —
  //   ein Nachladen darf einen begonnenen Text nie ueberschreiben.
  const anrede = bewerber?.vorname ? `Hallo ${bewerber.vorname},` : 'Hallo,';
  const signatur = 'Dein WeShip Team';
  useEffect(() => {
    if (!bewerber) return;
    setEmailText(prev => (prev ? prev : `${anrede}\n\n\n\n${signatur}`));
    setSmsText(prev => (prev ? prev : `${anrede} `));
  }, [bewerber?.id, anrede]);

  // ─── Status change (Bundle A: SoT) ───
  const changeStatus = async (status, extras = {}) => {
    setActionLoading(true);
    try {
      const res = await fj(`/api/recruiting/bewerber/${id}/status`, { status, ...extras }, 'PUT');
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Show success modal when MA was created via onboarding
        if (status === 'onboarding' && data.mitarbeiter_id) {
          try {
            const maRes = await f(`/api/mitarbeiter/${data.mitarbeiter_id}`);
            if (maRes.ok) {
              const ma = await maRes.json();
              setOnboardingSuccess({ id: ma.id, barcode: ma.barcode, stempel_pin: ma.stempel_pin, vorname: ma.vorname, nachname: ma.nachname });
            }
          } catch { /* */ }
        }
        load();
      }
    } catch { /* */ }
    setActionLoading(false);
  };

  // ─── Compose send ───
  const [sendError, setSendError] = useState(null);
  const sendCompose = async () => {
    setComposeSending(true);
    setSendError(null);
    try {
      let res;
      if (composeTab === 'email') {
        if (!emailBetreff.trim() || !emailText.trim()) { setComposeSending(false); return; }
        res = await fj(`/api/recruiting/bewerber/${id}/email`, { betreff: emailBetreff, text: emailText });
      } else if (composeTab === 'sms') {
        if (!smsText.trim()) { setComposeSending(false); return; }
        res = await fj(`/api/recruiting/bewerber/${id}/sms`, { text: smsText });
      } else {
        if (!notizText.trim()) { setComposeSending(false); return; }
        res = await fj(`/api/recruiting/bewerber/${id}/notiz`, { text: notizText });
      }
      if (res && !res.ok) {
        const d = await res.json().catch(() => ({}));
        setSendError(d.error || 'Versand fehlgeschlagen');
      } else {
        // Nach dem Senden wieder die sichtbare Vorbelegung, nicht ein leeres Feld —
        // sonst waere die naechste Nachricht ohne Anrede unterwegs (#1845).
        if (composeTab === 'email') { setEmailBetreff(''); setEmailText(`${anrede}\n\n\n\n${signatur}`); }
        else if (composeTab === 'sms') { setSmsText(`${anrede} `); }
        else { setNotizText(''); }
      }
      load();
    } catch { setSendError('Netzwerkfehler'); }
    setComposeSending(false);
  };

  // ─── Star rating ───
  const updateBewertung = async (bewertung) => {
    try {
      await fj(`/api/recruiting/bewerber/${id}/probearbeit`, { bewertung }, 'PUT');
      load();
    } catch { /* */ }
  };

  // ─── Vertrag senden ───
  const sendVertrag = async () => {
    if (!selectedTemplate) return;
    setActionLoading(true);
    try {
      const res = await fj(`/api/recruiting/bewerber/${id}/vertrag-senden`, { template_id: parseInt(selectedTemplate) });
      if (res.ok) { load(); setShowVertrag(false); }
      else { const err = await res.json().catch(() => ({})); alert(err.error || 'Fehler'); }
    } catch { /* */ }
    setActionLoading(false);
  };

  // ─── Document upload ───
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('datei', file);
    fd.append('typ', 'sonstiges');
    try {
      await f(`/api/recruiting/bewerber/${id}/dokument`, { method: 'POST', body: fd });
      load();
    } catch { /* */ }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteDoc = async (docId) => {
    await f(`/api/recruiting/dokumente/${docId}`, { method: 'DELETE' });
    load();
  };

  // ─── Interview create ───
  const createInterview = async (data) => {
    await fj(`/api/recruiting/bewerber/${id}/interview`, data);
    load();
    setShowInterview(false);
  };

  const deleteInterview = async (ivId) => {
    await f(`/api/recruiting/interviews/${ivId}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <div className="p-6 text-sm" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('common.loading', 'Laden...')}</div>;
  if (!bewerber) return <div className="p-6 text-sm" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('recruiting.nichtGefunden', 'Bewerber nicht gefunden')}</div>;

  // #959 Bundle A: Terminal- + Next-Status aus 7-Status-Spec ableiten.
  const currentStatus = canonicalStatus(bewerber.status);
  const isTerminal = ENDSTATUS.has(currentStatus);
  const currentStatusMeta = STATUS_MAP[currentStatus] || { color: '#6B7280', bg: '#F3F4F6' };
  const activeIdx = STATUS_DEF.findIndex(s => s.key === currentStatus);
  const nextStatus = !isTerminal && activeIdx >= 0 && activeIdx < STATUS_DEF.length - 1
    ? STATUS_DEF[activeIdx + 1]
    : null;

  return (
    <div className="p-6">
      {/* 70/30 Split */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        {/* ═══ LEFT SIDE (70%) ═══ */}
        <div className="space-y-6 min-w-0">
          {/* Header Card */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-4">
              {bewerber.foto_pfad ? (
                <img src={`${API}/api/recruiting/bewerber/${id}/foto`} alt="" className="w-16 h-16 rounded-xl object-cover border" style={{ borderColor: '#E5E7EB' }} />
              ) : (
                <div className="w-16 h-16 rounded-xl flex items-center justify-center text-xl font-bold"
                  style={{ background: currentStatusMeta.bg, color: currentStatusMeta.color }}>
                  {bewerber.vorname?.[0]}{bewerber.nachname?.[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {bewerber.vorname} {bewerber.nachname}
                  {bewerber.flex_bewerber_id && <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>Flex</span>}
                </h2>
                <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                  {bewerber.stelle_titel || bewerber.position || '-'} · {bewerber.quelle || '-'}
                </p>
              </div>
              {/* Star rating */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star}
                    onClick={() => updateBewertung(star)}
                    onMouseEnter={() => setHoverStar(star)}
                    onMouseLeave={() => setHoverStar(0)}
                    className="transition-colors"
                    style={{ color: star <= (hoverStar || bewerber.bewertung_sterne || 0) ? '#F59E0B' : '#D1D5DB' }}>
                    <IconStar filled={star <= (hoverStar || bewerber.bewertung_sterne || 0)} />
                  </button>
                ))}
              </div>
            </div>
            {/* Quick actions row */}
            <div className="flex gap-2 mt-4 flex-wrap">
              {nextStatus && (
                <button onClick={() => changeStatus(nextStatus.key)} disabled={actionLoading}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
                  style={{ background: nextStatus.color }}>
                  {t('recruiting.detail.weiterZu', 'Weiter zu')} {t(`recruiting.status.${nextStatus.key}`, nextStatus.key)}
                </button>
              )}
              {!isTerminal && (
                <button onClick={() => setShowAbsage(!showAbsage)} disabled={actionLoading}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-60"
                  style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                  {t('recruiting.absage', 'Absage')}
                </button>
              )}
              {bewerber.mitarbeiter_id && (
                <button onClick={() => navigate(`/mitarbeiter/${bewerber.mitarbeiter_id}`)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border"
                  style={{ borderColor: '#10B981', color: '#10B981' }}>
                  {t('recruiting.zumMitarbeiter', 'Zum Mitarbeiter')}
                </button>
              )}
            </div>
            {showAbsage && (
              <div className="mt-3 p-4 rounded-lg border space-y-3" style={{ borderColor: '#FCA5A5', background: '#FEF2F2' }}>
                <p className="text-xs font-semibold" style={{ color: '#991B1B' }}>{t('recruiting.absage.grund', 'Absage-Grund')}</p>
                <div className="space-y-1.5">
                  {[
                    { key: 'qualifikation', label: t('recruiting.absage.qualifikation', 'Qualifikation nicht passend') },
                    { key: 'keine_antwort', label: t('recruiting.absage.keineAntwort', 'Keine Rückmeldung') },
                    { key: 'anderer_kandidat', label: t('recruiting.absage.andererKandidat', 'Anderer Kandidat gewählt') },
                    { key: 'selbst_zurueckgezogen', label: t('recruiting.absage.selbstZurueckgezogen', 'Bewerber hat sich zurückgezogen') },
                    { key: 'sonstiges', label: t('recruiting.absage.sonstiges', 'Sonstiges') },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="absage_grund" value={opt.key}
                        checked={absageKategorie === opt.key}
                        onChange={() => setAbsageKategorie(opt.key)}
                        className="accent-[#EF4444]" />
                      <span className="text-xs" style={{ color: '#4B5563' }}>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <textarea value={absageKommentar} onChange={e => setAbsageKommentar(e.target.value)} rows={2}
                  className="w-full px-3 py-2 rounded-lg border text-sm resize-none" style={{ borderColor: '#E5E7EB' }}
                  placeholder={t('recruiting.absage.kommentar', 'Optionaler Kommentar') + '...'} />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={absageEmailSenden}
                    onChange={e => setAbsageEmailSenden(e.target.checked)}
                    className="accent-[#EF4444]" />
                  <span className="text-xs" style={{ color: '#4B5563' }}>{t('recruiting.absage.emailSenden', 'Absage-E-Mail senden')}</span>
                </label>
                <button onClick={async () => {
                  if (!absageKategorie) return;
                  setActionLoading(true);
                  try {
                    const res = await fj(`/api/recruiting/${id}/absage`, {
                      grund: absageKategorie,
                      kommentar: absageKommentar,
                      email_senden: absageEmailSenden,
                    });
                    if (res.ok) { load(); setShowAbsage(false); setAbsageKategorie(''); setAbsageKommentar(''); }
                    else { const err = await res.json().catch(() => ({})); alert(err.error || 'Fehler'); }
                  } catch { /* */ }
                  setActionLoading(false);
                }} disabled={actionLoading || !absageKategorie}
                  className="w-full px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60" style={{ background: '#EF4444' }}>
                  {t('recruiting.absageSenden', 'Absage bestätigen')}
                </button>
              </div>
            )}
          </div>

          {/* Stammdaten Card (editable) */}
          <StammdatenCard bewerber={bewerber} id={id} t={t} onUpdate={load} />

          {/* #962: Einstellungs-Prozess-Stepper entfernt — Onboarding-Status lebt jetzt als Sidebar-Liste rechts */}

          {/* Dokumente Card */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.detail.dokumente', 'Dokumente')} ({dokumente.length})
              </h3>
              <button onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: '#338edc' }}>
                <IconUpload /> {t('recruiting.detail.hochladen', 'Hochladen')}
              </button>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleDocUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
            </div>
            {/* Legacy CV */}
            {bewerber.lebenslauf_pfad && !dokumente.some(d => d.typ === 'lebenslauf') && (
              <a href={`${API}/api/recruiting/bewerber/${id}/lebenslauf`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 p-2.5 rounded-lg border mb-2 text-sm"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: '#338edc' }}>
                <IconDownload /> {t('recruiting.lebenslaufDownload', 'Lebenslauf herunterladen')}
              </a>
            )}
            {dokumente.length > 0 ? (
              <div className="space-y-1.5">
                {dokumente.map(d => (
                  <div key={d.id} className="flex items-center gap-3 p-2 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                    <IconFileText />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{d.dateiname}</p>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {d.typ} · {d.groesse_bytes ? `${Math.round(d.groesse_bytes / 1024)}KB` : ''} · {timeAgo(d.created_at)}
                      </p>
                    </div>
                    <a href={`${API}/api/recruiting/dokumente/${d.id}/download`} target="_blank" rel="noopener noreferrer"
                      className="flex-shrink-0" style={{ color: '#338edc' }}>
                      <IconDownload />
                    </a>
                    <button onClick={() => deleteDoc(d.id)} className="flex-shrink-0" style={{ color: '#EF4444' }}>
                      <IconTrash />
                    </button>
                  </div>
                ))}
              </div>
            ) : !bewerber.lebenslauf_pfad && (
              <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.keineDokumente', 'Keine Dokumente vorhanden')}
              </p>
            )}
          </div>

          {/* Interviews Card */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.detail.interviews', 'Interviews')} ({interviews.length})
              </h3>
              <button onClick={() => setShowInterview(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: '#338edc' }}>
                <IconCalendar /> {t('recruiting.detail.interviewPlanen', 'Planen')}
              </button>
            </div>
            {interviews.length > 0 ? (
              <div className="space-y-2">
                {interviews.map(iv => (
                  <div key={iv.id} className="flex items-center gap-3 p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#EBF4FD', color: '#338edc' }}>
                      <IconCalendar />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                        {iv.typ === 'vor_ort' ? 'Vor Ort' : iv.typ === 'video' ? 'Video' : 'Telefon'}
                        {iv.interviewer_name && <span className="font-normal" style={{ color: 'var(--text-muted, #9CA3AF)' }}> · {iv.interviewer_name}</span>}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {iv.datum ? formatDateTime(iv.datum) : '—'} · {iv.dauer_minuten}min
                        {iv.ort && ` · ${iv.ort}`}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{
                      background: iv.status === 'durchgefuehrt' ? '#ECFDF5' : iv.status === 'abgesagt' ? '#FEF2F2' : '#EBF4FD',
                      color: iv.status === 'durchgefuehrt' ? '#059669' : iv.status === 'abgesagt' ? '#EF4444' : '#338edc',
                    }}>
                      {iv.status === 'geplant' ? 'Geplant' : iv.status === 'durchgefuehrt' ? 'Durchgeführt' : 'Abgesagt'}
                    </span>
                    {iv.bewertung > 0 && (
                      <span className="text-xs tabular-nums flex-shrink-0" style={{ color: '#F59E0B' }}>
                        {'★'.repeat(iv.bewertung)}{'☆'.repeat(5 - iv.bewertung)}
                      </span>
                    )}
                    <button onClick={() => deleteInterview(iv.id)} className="flex-shrink-0" style={{ color: '#EF4444' }}>
                      <IconTrash />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.keineInterviews', 'Keine Interviews geplant')}
              </p>
            )}
            {showInterview && <InterviewModal t={t} onSubmit={createInterview} onClose={() => setShowInterview(false)} />}
          </div>

          {/* Compose Section */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] mb-4">
              {[
                { key: 'email', label: t('recruiting.compose.email', 'E-Mail') },
                { key: 'sms', label: t('recruiting.compose.sms', 'SMS') },
                { key: 'notiz', label: t('recruiting.compose.notiz', 'Notiz') },
              ].map(tb => (
                <button key={tb.key} onClick={() => setComposeTab(tb.key)}
                  className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                    composeTab === tb.key
                      ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]'
                      : 'hover:text-[var(--text-primary)]'
                  }`}
                  style={{ color: composeTab === tb.key ? 'var(--text-primary, #1A1A2E)' : 'var(--text-secondary, #4B5563)' }}
                >{tb.label}</button>
              ))}
            </div>

            {composeTab === 'email' && (
              <div className="space-y-2">
                <input value={emailBetreff} onChange={e => setEmailBetreff(e.target.value)}
                  placeholder={t('recruiting.compose.betreff', 'Betreff...')}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)' }} />
                <textarea value={emailText} onChange={e => setEmailText(e.target.value)} rows={8}
                  placeholder={t('recruiting.compose.emailText', 'Nachricht verfassen...')}
                  className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                  style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)' }} />
              </div>
            )}
            {composeTab === 'sms' && (
              <div className="space-y-2">
                <textarea value={smsText} onChange={e => setSmsText(e.target.value.slice(0, 160))} rows={3}
                  placeholder={t('recruiting.compose.smsText', 'SMS-Nachricht...')}
                  className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                  style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)' }} />
                <p className="text-xs text-right" style={{ color: smsText.length > 140 ? '#EF4444' : 'var(--text-muted, #9CA3AF)' }}>
                  {smsText.length}/160
                </p>
              </div>
            )}
            {composeTab === 'notiz' && (
              <textarea value={notizText} onChange={e => setNotizText(e.target.value)} rows={3}
                placeholder={t('recruiting.compose.notizText', 'Interne Notiz hinzufügen...')}
                className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)' }} />
            )}

            {sendError && (
              <div className="mt-2 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: '#FEF2F2', color: '#DC2626' }}>
                {sendError}
              </div>
            )}
            <div className="flex justify-end mt-3">
              <button onClick={sendCompose} disabled={composeSending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#338edc' }}>
                <IconSend />
                {composeTab === 'notiz'
                  ? t('recruiting.compose.addNotiz', 'Notiz hinzufügen')
                  : t('recruiting.compose.senden', 'Senden')}
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.timeline', 'Aktivitäten')} ({aktivitaeten.length})
            </h3>
            {aktivitaeten.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.keineAktivitaeten', 'Noch keine Aktivitäten')}
              </p>
            ) : (
              <div className="space-y-0">
                {aktivitaeten.map((a, idx) => {
                  const actCfg = AKT_CONFIG[a.typ] || AKT_CONFIG.status_change;
                  const ActIcon = actCfg.icon;
                  const isLast = idx === aktivitaeten.length - 1;
                  const raw = a.beschreibung || '';
                  const lines = raw.split('\n');
                  const firstLine = lines[0] || '';
                  // #960: Vollen Body anzeigen — Render-Box ist scrollbar (max-h-[300px] overflow-y-auto)
                  const isEmail = a.typ === 'email_out' || a.typ === 'email' || a.typ === 'email_in';
                  const bodyRaw = lines.length > 1 ? lines.slice(1).join('\n') : '';
                  const bodyFull = isEmail ? stripHtml(bodyRaw) : bodyRaw;
                  return (
                    <div key={a.id} className="flex gap-3 relative">
                      {!isLast && <div className="absolute left-[15px] top-[32px] bottom-0 w-px" style={{ background: 'var(--border-card, #E5E7EB)' }} />}
                      <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: actCfg.bg, color: actCfg.color }}>
                        <ActIcon />
                      </div>
                      <div className="flex-1 min-w-0 pb-4">
                        <p className="text-sm leading-snug" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {firstLine}
                          {a.typ === 'email_out' && a.email_status && (
                            <span className="inline-flex items-center ml-1.5" title={a.email_fehler || a.email_status}>
                              {(a.email_status === 'sent' || a.email_status === 'gesendet') && <IconCheckCircle />}
                              {(a.email_status === 'failed' || a.email_status === 'fehler') && <IconXCircle />}
                              {(a.email_status === 'queued' || a.email_status === 'held' || a.email_status === 'pending') && <IconClock />}
                            </span>
                          )}
                          {a.typ === 'sms_out' && a.versand_status && (
                            <span className="inline-flex items-center ml-1.5" title={a.versand_fehler || a.versand_status}>
                              {a.versand_status === 'sent' && <span style={{ color: '#10B981' }}><IconCheckCircle /></span>}
                              {a.versand_status === 'failed' && <span style={{ color: '#EF4444' }}><IconXCircle /></span>}
                            </span>
                          )}
                        </p>
                        {bodyFull && (
                          isEmail ? (
                            <div className="mt-2 p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-sm whitespace-pre-line leading-relaxed max-h-[300px] overflow-y-auto" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                              {bodyFull}
                            </div>
                          ) : (
                            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{bodyFull.split('\n').slice(0, 2).join(' ').substring(0, 200)}</p>
                          )
                        )}
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                          {a.erstellt_von_name && <span>{a.erstellt_von_name} &middot; </span>}
                          {timeAgo(a.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ RIGHT SIDEBAR (30%) ═══ */}
        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          {/* #959 Bundle A: Status-Stepper (7-Status-SoT) statt dynamic Pipeline-Stepper */}
          {!isTerminal && (
            <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.status', 'Status')}
              </h4>
              <div className="space-y-1">
                {STATUS_DEF.map((step, i) => {
                  const isCurrent = step.key === currentStatus;
                  const isDone = activeIdx >= 0 && i < activeIdx;
                  return (
                    <button key={step.key}
                      onClick={() => !isCurrent && !isTerminal && changeStatus(step.key)}
                      disabled={actionLoading}
                      className="flex items-center gap-2.5 w-full text-left py-1 disabled:opacity-60 hover:opacity-80 transition-opacity">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: isCurrent ? step.color : isDone ? '#10B981' : 'var(--bg-inset, #F8F9FC)',
                          border: `2px solid ${isCurrent ? step.color : isDone ? '#10B981' : 'var(--border-card, #E5E7EB)'}`,
                          color: isCurrent || isDone ? 'white' : 'var(--text-muted, #9CA3AF)',
                        }}>
                        {isDone ? <IconCheck /> : <span className="text-[9px] font-bold">{i + 1}</span>}
                      </div>
                      <span className="text-xs font-medium" style={{
                        color: isCurrent ? step.color : isDone ? 'var(--text-secondary, #4B5563)' : 'var(--text-muted, #9CA3AF)'
                      }}>
                        {t(`recruiting.status.${step.key}`, step.key)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Context actions */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              {t('recruiting.detail.naechsterSchritt', 'Nächster Schritt')}
            </h4>
            <div className="space-y-2">
              {/* Vertrag senden (when stammdaten bestätigt + kein Vertrag) */}
              {unterprozess?.stammdaten?.status === 'bestaetigt' && !unterprozess?.vertrag?.status && (
                <ActionBtn label={t('recruiting.detail.vertragSenden', 'Vertrag erstellen & senden')} color="#338edc"
                  onClick={() => {
                    f('/api/vertraege/templates').then(r => r.json()).then(d => setTemplates(d.templates || [])).catch(() => {});
                    setShowVertrag(true);
                  }} loading={actionLoading} />
              )}

              {/* Onboarding starten (when vertrag unterschrieben) */}
              {unterprozess?.vertrag?.status === 'unterschrieben' && !bewerber.mitarbeiter_id && (
                <ActionBtn label={t('recruiting.detail.onboardingStarten', 'Onboarding starten')} color="#10B981"
                  onClick={() => changeStatus('onboarding')} loading={actionLoading} />
              )}

              {/* Vertrag template inline */}
              {showVertrag && (
                <div className="p-3 rounded-lg border space-y-2" style={{ borderColor: '#93C5FD', background: '#EFF6FF' }}>
                  <p className="text-xs font-medium" style={{ color: '#1E40AF' }}>{t('recruiting.detail.vertragTemplate', 'Vorlage')}:</p>
                  {templates.length === 0 ? (
                    <p className="text-xs" style={{ color: '#6B7280' }}>{t('recruiting.detail.keineTemplates', 'Keine aktiven Templates')}</p>
                  ) : (
                    <>
                      <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}>
                        <option value="">Template wählen...</option>
                        {templates.filter(tmpl => tmpl.aktiv).map(tmpl => (
                          <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={() => setShowVertrag(false)} className="flex-1 px-3 py-1.5 rounded-lg text-sm border" style={{ borderColor: '#E5E7EB', color: '#4B5563' }}>
                          {t('common.cancel', 'Abbrechen')}
                        </button>
                        <button onClick={sendVertrag} disabled={actionLoading || !selectedTemplate}
                          className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60" style={{ background: '#338edc' }}>
                          {t('recruiting.detail.vertragSendenBtn', 'Senden')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stammdaten Sidebar Summary */}
          <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.info', 'Informationen')}
              </h4>
              {bewerber.stammdaten_ausgefuellt ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#ECFDF5', color: '#059669' }}>
                  &#10003; {t('recruiting.detail.komplett', 'Komplett')}
                </span>
              ) : (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FFF7ED', color: '#D97706' }}>
                  &#9711; {t('recruiting.detail.ausstehend', 'Ausstehend')}
                </span>
              )}
            </div>
            <div className="space-y-1.5 text-sm">
              <InfoRow label="E-Mail" value={bewerber.email} link={bewerber.email ? `mailto:${bewerber.email}` : null} />
              <InfoRow label={t('recruiting.detail.telefon', 'Telefon')} value={bewerber.telefon} link={bewerber.telefon ? `tel:${bewerber.telefon}` : null} />
              <InfoRow label={t('recruiting.quelle', 'Quelle')} value={bewerber.quelle} />
              <InfoRow label={t('recruiting.detail.standort', 'Standort')} value={bewerber.standort_name} />
              {bewerber.fruehester_start && <InfoRow label={t('recruiting.detail.fruehesterStart', 'Frühester Start')} value={formatDate(bewerber.fruehester_start)} />}
              {bewerber.svnr && <InfoRow label="SVNR" value={bewerber.svnr} />}
              {bewerber.iban && <InfoRow label="IBAN" value={bewerber.iban} />}
              {(bewerber.strasse || bewerber.ort) && <InfoRow label={t('recruiting.detail.adresse', 'Adresse')} value={[bewerber.strasse, [bewerber.plz, bewerber.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')} />}
              {bewerber.geburtsdatum && <InfoRow label={t('recruiting.detail.geburtsdatum', 'Geburtsdatum')} value={formatDate(bewerber.geburtsdatum)} />}
              {bewerber.brutto_monatlich && <InfoRow label={t('recruiting.detail.brutto', 'Brutto/Monat')} value={`${Number(bewerber.brutto_monatlich).toLocaleString('de-AT')} \u20AC`} />}
              {bewerber.wochenstunden && <InfoRow label={t('recruiting.detail.stunden', 'Wochenstunden')} value={bewerber.wochenstunden} />}
              <InfoRow label={t('recruiting.datum', 'Eingegangen')} value={formatDate(bewerber.created_at)} />
            </div>
          </div>

          {/* #962: ONBOARDING-STATUS — sichtbar nur bei status IN ('zugesagt','aktiv') */}
          {['zugesagt', 'aktiv'].includes(bewerber.status) && (
            <OnboardingStatusList bewerber={bewerber} unterprozess={unterprozess} t={t} />
          )}

          {/* Probearbeit Card */}
          {(bewerber.probearbeit_datum || bewerber.probearbeit_bewertung) && (
            <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.detail.probearbeit', 'Probearbeit')}
              </h4>
              {bewerber.probearbeit_datum && (
                <p className="text-sm mb-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {t('recruiting.detail.probearbeitDatum', 'Datum')}: <strong>{formatDate(bewerber.probearbeit_datum)}</strong>
                </p>
              )}
              <div className="flex items-center gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star}
                    onClick={() => updateBewertung(star)}
                    onMouseEnter={() => setHoverStar(star)}
                    onMouseLeave={() => setHoverStar(0)}
                    className="transition-colors"
                    style={{ color: star <= (hoverStar || bewerber.probearbeit_bewertung || 0) ? '#F59E0B' : '#D1D5DB' }}>
                    <IconStar filled={star <= (hoverStar || bewerber.probearbeit_bewertung || 0)} />
                  </button>
                ))}
              </div>
              {bewerber.probearbeit_notiz && (
                <p className="text-xs p-2 rounded-lg" style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary, #4B5563)' }}>
                  {bewerber.probearbeit_notiz}
                </p>
              )}
            </div>
          )}

          {/* Delete Button */}
          {!bewerber.mitarbeiter_id && (
            <div className="pt-4 border-t" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
              <button onClick={() => setConfirmDelete(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-[10px] border text-sm font-medium transition-colors hover:bg-red-50"
                style={{ borderColor: '#FECACA', color: '#DC2626' }}>
                <IconTrash />
                {t('recruiting.bewerber.deleteButton', 'Löschen')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm dialog: Löschen ── */}
      {/* Onboarding Success Modal */}
      {onboardingSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setOnboardingSuccess(null)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border w-full max-w-md mx-4 p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)' }} onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                {t('recruiting.onboarding.successTitle', 'Mitarbeiter erfolgreich erstellt')}
              </h3>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.onboarding.successMsg', '{name} wurde als Mitarbeiter angelegt.').replace('{name}', `${onboardingSuccess.vorname} ${onboardingSuccess.nachname}`)}
              </p>
            </div>
            <div className="space-y-2 mb-5 p-3 rounded-lg" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('recruiting.onboarding.maId', 'MA-ID')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>#{onboardingSuccess.id}</span>
              </div>
              {onboardingSuccess.barcode && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('recruiting.onboarding.barcode', 'Barcode')}</span>
                  <span className="font-mono font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{onboardingSuccess.barcode}</span>
                </div>
              )}
              {onboardingSuccess.stempel_pin && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('recruiting.onboarding.pin', 'Stempel-PIN')}</span>
                  <span className="font-mono font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{onboardingSuccess.stempel_pin}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOnboardingSuccess(null)}
                className="flex-1 px-4 py-2 rounded-[10px] border text-sm font-medium"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {t('common.close', 'Schließen')}
              </button>
              <button onClick={() => navigate(`/mitarbeiter/${onboardingSuccess.id}`)}
                className="flex-1 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors">
                {t('recruiting.onboarding.goToProfile', 'Zum Profil')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmDelete(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border w-full max-w-md mx-4 p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('recruiting.bewerber.deleteConfirmTitle', 'Bewerber löschen')}
            </h3>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.bewerber.deleteConfirmText', '{name} wirklich löschen? Alle Aktivitäten, Dokumente und Interviews werden ebenfalls gelöscht.').replace('{name}', `${bewerber.vorname} ${bewerber.nachname}`)}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-[10px] border text-sm font-medium"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button onClick={async () => {
                setConfirmDelete(false);
                try {
                  const res = await f(`/api/recruiting/bewerber/${id}`, { method: 'DELETE' });
                  if (res.ok) navigate('/recruiting/bewerber');
                  else { const d = await res.json().catch(() => ({})); alert(d.error || 'Fehler'); }
                } catch { alert('Netzwerkfehler'); }
              }}
                className="px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                {t('recruiting.bewerber.deleteButton', 'Löschen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── #962: Onboarding-Status-Liste (Sidebar, sichtbar bei status IN ('zugesagt','aktiv')) ───
// Festanstellung: 5-Punkt-Liste; Flex-Worker: 2-Punkt-Liste. Kein Stepper, keine Action-Buttons.
function OnboardingStatusList({ bewerber: b, unterprozess: up, t }) {
  const isFlex = b.kategorie === 'flex';
  const sd = up?.stammdaten || {};
  const v = up?.vertrag || {};

  let items;
  if (isFlex) {
    items = [
      {
        key: 'flex_stammdaten',
        label: t('recruiting.onboarding.stammdatenAusgefuellt', 'Stammdaten ausgefüllt'),
        done: !!b.stammdaten_ausgefuellt || !!b.stammdaten_am,
        ts: b.stammdaten_am,
      },
      {
        key: 'flex_pool',
        label: t('recruiting.onboarding.imPoolSeit', 'Im Pool seit'),
        done: b.status === 'aktiv',
        ts: b.eingestellt_am,
      },
    ];
  } else {
    const stammdatenDone = sd.status === 'ausgefuellt' || sd.status === 'bestaetigt' || !!b.stammdaten_ausgefuellt;
    const vertragErstellt = !!v.status || !!b.vertrag_id;
    const vertragVersendet = v.status === 'gesendet' || v.status === 'unterschrieben';
    const vertragSigned = v.status === 'unterschrieben';
    const eingestellt = !!b.eingestellt_am || b.status === 'aktiv' || !!b.mitarbeiter_id;
    items = [
      { key: 'fest_stammdaten', label: t('recruiting.onboarding.stammdatenErfasst', 'Stammdaten erfasst'), done: stammdatenDone, ts: sd.ausgefuellt_am || b.stammdaten_am },
      { key: 'fest_vertrag_erstellt', label: t('recruiting.onboarding.vertragErstellt', 'Vertrag erstellt'), done: vertragErstellt, ts: v.created_at },
      { key: 'fest_versendet', label: t('recruiting.onboarding.vertragVersendet', 'Vertrag versendet'), done: vertragVersendet, ts: v.gesendet_am },
      { key: 'fest_unterschrieben', label: t('recruiting.onboarding.vertragUnterschrieben', 'Vertrag unterschrieben'), done: vertragSigned, ts: v.unterschrift_am || v.unterschrieben_am },
      { key: 'fest_eingestellt', label: t('recruiting.onboarding.eingestellt', 'Eingestellt'), done: eingestellt, ts: b.eingestellt_am },
    ];
  }

  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

  return (
    <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
      <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
        {t('recruiting.onboarding.title', 'Onboarding-Status')}
      </h4>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="flex items-center justify-between gap-3 text-sm">
            <span style={{ color: 'var(--text-secondary, #4B5563)' }}>{item.label}</span>
            {item.done ? (
              <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: '#059669' }}>
                <IconCheck />
                {fmtDate(item.ts) || ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                <span className="text-xs">⏳</span>
                {t('recruiting.onboarding.ausstehend', 'Ausstehend')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Sub-components ───

function ActionBtn({ label, color, onClick, loading }) {
  return (
    <button onClick={onClick} disabled={loading}
      className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
      style={{ background: color }}>
      {label}
    </button>
  );
}

function InfoRow({ label, value, link }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{label}</span>
      {link ? (
        <a href={link} className="text-xs font-medium text-right truncate" style={{ color: '#338edc' }}>{value}</a>
      ) : (
        <span className="text-xs font-medium text-right truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{value}</span>
      )}
    </div>
  );
}

function StammdatenCard({ bewerber, id, t, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      vorname: bewerber.vorname || '', nachname: bewerber.nachname || '',
      email: bewerber.email || '', telefon: bewerber.telefon || '',
      position: bewerber.position || '', quelle: bewerber.quelle || '',
      brutto_monatlich: bewerber.brutto_monatlich || '', wochenstunden: bewerber.wochenstunden || '',
      fruehester_start: bewerber.fruehester_start ? bewerber.fruehester_start.split('T')[0] : '',
    });
    setEditing(true);
  };

  const save = async () => {
    await fj(`/api/recruiting/bewerber/${id}`, form, 'PUT');
    setEditing(false);
    onUpdate();
  };

  if (!editing) {
    return (
      <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {t('recruiting.detail.stammdaten', 'Stammdaten')}
          </h3>
          <button onClick={startEdit} className="text-xs font-medium" style={{ color: '#338edc' }}>
            {t('common.edit', 'Bearbeiten')}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <StammdatenField label={t('recruiting.vorname', 'Vorname')} value={bewerber.vorname} />
          <StammdatenField label={t('recruiting.nachname', 'Nachname')} value={bewerber.nachname} />
          <StammdatenField label="E-Mail" value={bewerber.email} />
          <StammdatenField label={t('recruiting.detail.telefon', 'Telefon')} value={bewerber.telefon} />
          <StammdatenField label={t('recruiting.position', 'Position')} value={bewerber.position} />
          <StammdatenField label={t('recruiting.quelle', 'Quelle')} value={bewerber.quelle} />
          {bewerber.brutto_monatlich && <StammdatenField label={t('recruiting.detail.brutto', 'Brutto/Monat')} value={`${bewerber.brutto_monatlich} \u20AC`} />}
          {bewerber.wochenstunden && <StammdatenField label={t('recruiting.detail.stunden', 'Wochenstunden')} value={bewerber.wochenstunden} />}
          {bewerber.svnr && <StammdatenField label="SVNR" value={bewerber.svnr} />}
          {bewerber.iban && <StammdatenField label="IBAN" value={bewerber.iban} />}
          {(bewerber.strasse || bewerber.ort) && <StammdatenField label={t('recruiting.detail.adresse', 'Adresse')} value={[bewerber.strasse, [bewerber.plz, bewerber.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')} />}
        </div>
        {bewerber.anschreiben && (
          <div className="mt-3 p-2.5 rounded-lg text-xs" style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary, #4B5563)' }}>
            <strong>{t('recruiting.anschreiben', 'Anschreiben')}:</strong><br />
            {bewerber.anschreiben}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border p-5 dark:bg-[var(--bg-card)]" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
        {t('recruiting.detail.stammdatenBearbeiten', 'Stammdaten bearbeiten')}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <EditField label={t('recruiting.vorname', 'Vorname')} value={form.vorname} onChange={v => setForm(p => ({ ...p, vorname: v }))} />
        <EditField label={t('recruiting.nachname', 'Nachname')} value={form.nachname} onChange={v => setForm(p => ({ ...p, nachname: v }))} />
        <EditField label="E-Mail" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} />
        <EditField label={t('recruiting.detail.telefon', 'Telefon')} value={form.telefon} onChange={v => setForm(p => ({ ...p, telefon: v }))} />
        <EditField label={t('recruiting.position', 'Position')} value={form.position} onChange={v => setForm(p => ({ ...p, position: v }))} />
        <EditField label={t('recruiting.quelle', 'Quelle')} value={form.quelle} onChange={v => setForm(p => ({ ...p, quelle: v }))} />
        <EditField label={t('recruiting.detail.brutto', 'Brutto/Monat')} value={form.brutto_monatlich} onChange={v => setForm(p => ({ ...p, brutto_monatlich: v }))} type="number" />
        <EditField label={t('recruiting.detail.stunden', 'Wochenstunden')} value={form.wochenstunden} onChange={v => setForm(p => ({ ...p, wochenstunden: v }))} type="number" />
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setEditing(false)} className="flex-1 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#4B5563' }}>
          {t('common.cancel', 'Abbrechen')}
        </button>
        <button onClick={save} className="flex-1 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: '#338edc' }}>
          {t('common.save', 'Speichern')}
        </button>
      </div>
    </div>
  );
}

function StammdatenField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{label}</p>
      <p className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{value || '—'}</p>
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
    </div>
  );
}

function InterviewModal({ t, onSubmit, onClose }) {
  const [form, setForm] = useState({ typ: 'vor_ort', datum: '', dauer_minuten: 30, ort: '', notizen: '' });

  return (
    <div className="mt-3 p-3 rounded-lg border space-y-2" style={{ borderColor: '#93C5FD', background: '#EFF6FF' }}>
      <p className="text-xs font-medium" style={{ color: '#1E40AF' }}>{t('recruiting.detail.interviewPlanen', 'Interview planen')}</p>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.typ} onChange={e => setForm(p => ({ ...p, typ: e.target.value }))}
          className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}>
          <option value="vor_ort">Vor Ort</option>
          <option value="video">Video</option>
          <option value="telefon">Telefon</option>
        </select>
        <input type="number" value={form.dauer_minuten} onChange={e => setForm(p => ({ ...p, dauer_minuten: parseInt(e.target.value) || 30 }))}
          placeholder="Dauer (Min)" className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
      </div>
      <input type="datetime-local" value={form.datum} onChange={e => setForm(p => ({ ...p, datum: e.target.value }))}
        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
      <input value={form.ort} onChange={e => setForm(p => ({ ...p, ort: e.target.value }))}
        placeholder={t('recruiting.detail.interviewOrt', 'Ort')} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-3 py-1.5 rounded-lg text-sm border" style={{ borderColor: '#E5E7EB', color: '#4B5563' }}>
          {t('common.cancel', 'Abbrechen')}
        </button>
        <button onClick={() => onSubmit(form)}
          className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-white" style={{ background: '#338edc' }}>
          {t('recruiting.detail.interviewErstellen', 'Erstellen')}
        </button>
      </div>
    </div>
  );
}
